/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { FormattedNumber } from 'react-intl'
import { round } from 'lodash'

import { EuiFlexGroup, EuiFlexItem } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

import type { FunctionComponent } from 'react'

interface Props {
  dp?: number
  withSymbol?: boolean
  value: number
  unit?: 'cents' | 'centicents' | 'none'
  changeRate?: string
  symbolPosition?: 'before' | 'after'
}

const CuiElasticConsumptionUnits: FunctionComponent<Props> = ({
  dp = 2,
  withSymbol = true,
  unit = `centicents`,
  value,
  changeRate,
  symbolPosition = 'after',
}) => {
  const multiplier = {
    centicents: 10000,
    cents: 100,
    none: 1,
  }[unit]
  const roundedValue = round(Math.abs(value) / multiplier, dp)

  if (!withSymbol) {
    return <FormattedNumber value={roundedValue} minimumFractionDigits={dp} />
  }

  return (
    <EuiFlexGroup
      gutterSize='xs'
      alignItems='baseline'
      className='elastic-consumption-units'
      responsive={false}
      direction={symbolPosition === 'after' ? 'row' : 'rowReverse'}
    >
      <EuiFlexItem
        grow={false}
        className='elastic-consumption-units-unit'
        style={{ wordBreak: 'normal' }}
      >
        <FormattedNumber value={roundedValue} minimumFractionDigits={dp} />
      </EuiFlexItem>
      <EuiFlexItem
        grow={false}
        className='elastic-consumption-units-unit'
        style={{ wordBreak: 'normal' }}
      >
        <FormattedMessage id='elastic-consumption-units' defaultMessage='ECU' />
        {changeRate && <span> / {changeRate}</span>}
      </EuiFlexItem>
    </EuiFlexGroup>
  )
}

export default CuiElasticConsumptionUnits
